import dayjs from "dayjs";

/**
 * Date format options supported by the application
 */
export type DateFormatOption = "DD/MM/YYYY" | "MM/DD/YYYY" | "YYYY-MM-DD";

/**
 * Default date format (US format)
 */
export const DEFAULT_DATE_FORMAT: DateFormatOption = "DD/MM/YYYY";

/**
 * Format a date according to the specified format
 * @param date - Date to format (Date object, string, or timestamp)
 * @param format - Date format string (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string | number | null | undefined, format?: DateFormatOption): string => {
  if (!date) return "N/A";

  try {
    const dateFormat = format || DEFAULT_DATE_FORMAT;
    return dayjs(date).format(dateFormat);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Format a date using company-specific date format preference
 * @param date - Date to format
 * @param companyDateFormat - Company's preferred date format
 * @returns Formatted date string
 */
export const formatCompanyDate = (
  date: Date | string | number | null | undefined,
  companyDateFormat?: string,
): string => {
  if (!date) return "N/A";

  try {
    const format = (companyDateFormat as DateFormatOption) || DEFAULT_DATE_FORMAT;
    return dayjs(date).format(format);
  } catch (error) {
    console.error("Error formatting company date:", error);
    return "Invalid Date";
  }
};

/**
 * Format a date with time according to the specified format
 * @param date - Date to format
 * @param dateFormat - Date format string
 * @returns Formatted date and time string
 */
export const formatDateTime = (
  date: Date | string | number | null | undefined,
  dateFormat?: DateFormatOption,
): string => {
  if (!date) return "N/A";

  try {
    const dateStr = dateFormat || DEFAULT_DATE_FORMAT;
    return dayjs(date).format(`${dateStr} h:mm A`);
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return "Invalid Date";
  }
};

/**
 * Format a date and time using company-specific date format preference
 * @param date - Date to format
 * @param companyDateFormat - Company's preferred date format
 * @returns Formatted date and time string
 */
export const formatCompanyDateTime = (
  date: Date | string | number | null | undefined,
  companyDateFormat?: string,
): string => {
  if (!date) return "N/A";

  try {
    const dateStr = (companyDateFormat as DateFormatOption) || DEFAULT_DATE_FORMAT;
    return dayjs(date).format(`${dateStr} h:mm A`);
  } catch (error) {
    console.error("Error formatting company datetime:", error);
    return "Invalid Date";
  }
};

/**
 * Get the date format options for Select component
 */
export const DATE_FORMAT_OPTIONS = [
  { label: "MM/DD/YYYY (US)", value: "MM/DD/YYYY" },
  { label: "DD/MM/YYYY (India, UK)", value: "DD/MM/YYYY" },
  { label: "YYYY-MM-DD (ISO)", value: "YYYY-MM-DD" },
];

/**
 * Parse a date string according to a specific format
 * @param dateString - Date string to parse
 * @param format - Expected format of the input string
 * @returns dayjs object or null if invalid
 */
export const parseDate = (dateString: string, format?: DateFormatOption) => {
  if (!dateString) return null;

  try {
    const dateFormat = format || DEFAULT_DATE_FORMAT;
    return dayjs(dateString, dateFormat);
  } catch (error) {
    console.error("Error parsing date:", error);
    return null;
  }
};
